import { lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router';

import { BlokkieThemeProvider } from '@tikkie/blokkie';

import './index.css';
import './libs/i18n.ts';

import { ClaimLayout } from './layouts/claim-layout.tsx';
import { ErrorLayout } from './layouts/error-layout.tsx';
import { LandingLayout } from './layouts/landing-layout.tsx';
import { CampaignSyncLayout } from './layouts/campaign-sync-layout.tsx';

import { initializeAnalyticsProvider } from './libs/ga4.ts';
import LandingRoute from './routes/landing.tsx';

const IbanRoute = lazy(() => import('./routes/iban.tsx'));
const FullNameRoute = lazy(() => import('./routes/full-name.tsx'));
const SummaryRoute = lazy(() => import('./routes/summary.tsx'));
const CameraRoute = lazy(() => import('./routes/camera.tsx'));
const NotFoundRoute = lazy(() => import('./routes/not-found-error.tsx'));
const InternalServerErrorRoute = lazy(
  () => import('./routes/internal-server-error.tsx')
);
const SessionRoute = lazy(() => import('./routes/session.tsx'));
const SessionPendingRoute = lazy(() => import('./routes/session-pending.tsx'));
const SuccessRoute = lazy(() => import('./routes/success.tsx'));
const GenericErrorRoute = lazy(
  () => import('./routes/error/generic-error.tsx')
);
const NoConnectionErrorRoute = lazy(
  () => import('./routes/error/no-connection-error.tsx')
);

const SessionNotFoundErrorRoute = lazy(
  () => import('./routes/error/session-not-found-error.tsx')
);

const TokenErrorGenericRoute = lazy(
  () => import('./routes/error/token-generic-error.tsx')
);
const CampaignNotValidRoute = lazy(
  () => import('./routes/error/campaign-not-valid.tsx')
);
const CampaignInactiveRoute = lazy(
  () => import('./routes/error/campaign-inactive.tsx')
);
const TokenExpiredRoute = lazy(
  () => import('./routes/error/token-expired.tsx')
);
const TokenClaimedRoute = lazy(
  () => import('./routes/error/token-claimed.tsx')
);
const MaxReachedRoute = lazy(() => import('./routes/error/max-reached.tsx'));

async function enableMocking() {
  if (!import.meta.env.DEV) {
    return;
  }

  const { worker } = await import('./mocks/browser');

  return worker.start();
}

enableMocking().then(() => {
  initializeAnalyticsProvider();

  createRoot(document.getElementById('root')!).render(
    <BlokkieThemeProvider theme="retail">
      <BrowserRouter>
        <Routes>
          {[
            '/recycle/:campaignType/:campaignId',
            '/recycle/:campaignType/:campaignId/v2/:token',
            '/:campaignType/:campaignId/:token',
            '/recycle/:campaignType/:campaignId/:token',
          ].map((path) => (
            <Route path={path} key={path}>
              <Route element={<LandingLayout />}>
                <Route index element={<LandingRoute />} />
              </Route>

              <Route element={<CampaignSyncLayout />}>
                <Route element={<ClaimLayout />}>
                  <Route path="iban" element={<IbanRoute />} />
                  <Route path="full-name" element={<FullNameRoute />} />
                  <Route path="summary" element={<SummaryRoute />} />
                  <Route path="session" element={<SessionRoute />} />
                  <Route
                    path="session/pending/:redemptionId"
                    element={<SessionPendingRoute />}
                  />
                  <Route path="success" element={<SuccessRoute />} />
                </Route>
              </Route>

              <Route element={<ClaimLayout />}>
                <Route path="error" element={<GenericErrorRoute />} />
              </Route>

              <Route path="error" element={<ClaimLayout />}>
                <Route path="*" element={<GenericErrorRoute />} />

                <Route
                  path="no-connection"
                  element={<NoConnectionErrorRoute />}
                />

                <Route
                  path="session-not-found"
                  element={<SessionNotFoundErrorRoute />}
                />

                <Route
                  path="token-error-generic"
                  element={<TokenErrorGenericRoute />}
                />

                <Route
                  path="campaign-not-valid"
                  element={<CampaignNotValidRoute />}
                />

                <Route
                  path="campaign-inactive"
                  element={<CampaignInactiveRoute />}
                />

                <Route path="token-expired" element={<TokenExpiredRoute />} />

                <Route path="token-claimed" element={<TokenClaimedRoute />} />
                <Route path="max-reached" element={<MaxReachedRoute />} />
              </Route>

              <Route element={<ClaimLayout backgroundColor="black" />}>
                <Route path="camera" element={<CameraRoute />} />
              </Route>
            </Route>
          ))}

          <Route element={<ErrorLayout />}>
            <Route path="/500" element={<InternalServerErrorRoute />} />
            <Route path="/404" element={<NotFoundRoute />} />
            <Route
              path="/recycle/:campaignType/:campaignId/v2"
              element={<NotFoundRoute />}
            />
            <Route path="*" element={<NotFoundRoute />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </BlokkieThemeProvider>
  );
});
